// The properties of a `chat_turn` event. Product analytics sees only what the
// server cannot: where the turn was started from and whether the person
// stopped it. Volume, latency, model and tool use are recorded server-side.
import { CHAT_SURFACE, CHAT_TRIGGER } from "./surfaceVocabulary.js";

// A slash command is a product action; anything else is a prompt. The command
// word itself is user text and stays out. A caller that knows better, such as
// a starter question or a continuation, passes its own trigger.
export function chatTurnTrigger(triggerText, trigger = null) {
    if (trigger) return trigger;
    return typeof triggerText === "string" && triggerText.trimStart().startsWith("/")
        ? CHAT_TRIGGER.SLASH
        : CHAT_TRIGGER.PROMPT;
}

// The chats whose current turn the person stopped. The agent settles a
// stopped run with an "aborted" message instead of throwing, so the turn's
// own error handling never sees the stop; the Stop controls mark the chat and
// the end of the turn reads the mark.
export function createTurnStops() {
    const stopped = new Set();
    return {
        begin(chatId) {
            stopped.delete(chatId);
        },
        stop(chatId) {
            stopped.add(chatId);
        },
        end(chatId) {
            return stopped.delete(chatId);
        },
    };
}

export function chatTurnProperties({ trigger = null, aborted = false, docked = false } = {}) {
    return {
        trigger: trigger ?? CHAT_TRIGGER.PROMPT,
        aborted: Boolean(aborted),
        surface: docked ? CHAT_SURFACE.DOCKED : CHAT_SURFACE.OVERLAY,
    };
}

// One turn's `chat_turn` event, sent once when the run is torn down,
// whichever path tears it down first: the turn's own `finally`, or a Stop
// control forcing a hung run closed while its request never settles.
export function startChatTurn({ chatId, trigger = null, stops, send }) {
    stops.begin(chatId);
    let abortedByError = false;
    let sent = false;
    return {
        failed(error) {
            if (error?.name === "AbortError") abortedByError = true;
        },
        end({ docked = false } = {}) {
            if (sent) return;
            sent = true;
            send(chatTurnProperties({ trigger, aborted: abortedByError || stops.end(chatId), docked }));
        },
    };
}
